import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  MessageSquare,
  Search,
  Loader2,
  Star,
  CheckCircle,
  XCircle,
  Clock,
  MoreVertical,
  MessageCircle,
  Trash2,
  Package,
  User,
  Calendar,
  ThumbsUp,
  BadgeCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';

type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export default function ReviewsPage() {
  const { t } = useTranslation('reviews');
  const canEdit = usePermission('REVIEWS', 'EDIT');
  const canDelete = usePermission('REVIEWS', 'DELETE');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | 'ALL'>('PENDING');
  const [page, setPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [showResponseModal, setShowResponseModal] = useState(false);

  const { data, isLoading, refetch } = trpc.review.list.useQuery({
    page,
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    search: search || undefined,
  });

  const { data: stats } = trpc.review.adminStats.useQuery();

  const moderateMutation = trpc.review.moderate.useMutation({
    onSuccess: () => refetch(),
  });

  const respondMutation = trpc.review.respond.useMutation({
    onSuccess: () => {
      setShowResponseModal(false);
      setResponseText('');
      setSelectedReview(null);
      refetch();
    },
  });

  const deleteMutation = trpc.review.delete.useMutation({
    onSuccess: () => refetch(),
  });

  const handleApprove = (id: string) => {
    moderateMutation.mutate({ id, status: 'APPROVED' });
  };

  const handleReject = (id: string) => {
    moderateMutation.mutate({ id, status: 'REJECTED' });
  };

  const handleOpenResponse = (id: string, existingResponse?: string | null) => {
    setSelectedReview(id);
    setResponseText(existingResponse || '');
    setShowResponseModal(true);
  };

  const handleSubmitResponse = () => {
    if (!selectedReview || !responseText.trim()) return;
    respondMutation.mutate({ id: selectedReview, responseContent: responseText });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
            <Clock className="h-3 w-3" />
            {t('status.pending')}
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
            <CheckCircle className="h-3 w-3" />
            {t('status.approved')}
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            <XCircle className="h-3 w-3" />
            {t('status.rejected')}
          </span>
        );
      default:
        return null;
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/50'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.total')}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 p-2">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.pending')}</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.approved')}</p>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-2">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.rejected')}</p>
                <p className="text-2xl font-bold">{stats.rejected}</p>
              </div>
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2">
                <Star className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('stats.avgRating')}</p>
                <p className="text-2xl font-bold">{stats.averageRating}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-full border border-border bg-card pl-10 pr-4 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
          />
        </div>
        <div className="flex gap-2">
          {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`h-8 px-4 text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t(`filter.${status.toLowerCase()}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : data?.reviews.length === 0 ? (
          <div className="border border-border bg-card p-12 text-center">
            <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium text-foreground">{t('noReviews')}</h3>
            <p className="mt-2 text-muted-foreground">{t('noReviewsDesc')}</p>
          </div>
        ) : (
          data?.reviews.map((review) => (
            <div key={review.id} className="border border-border bg-card p-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 items-center justify-center bg-muted">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {review.customer.firstName} {review.customer.lastName}
                      </span>
                      {review.isVerified && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <BadgeCheck className="h-3 w-3" />
                          {t('verified')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{review.customer.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(review.status)}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit && review.status === 'PENDING' && (
                        <>
                          <DropdownMenuItem onClick={() => handleApprove(review.id)}>
                            <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                            {t('actions.approve')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleReject(review.id)}>
                            <XCircle className="mr-2 h-4 w-4 text-red-500" />
                            {t('actions.reject')}
                          </DropdownMenuItem>
                        </>
                      )}
                      {canEdit && review.status === 'APPROVED' && (
                        <DropdownMenuItem
                          onClick={() => handleOpenResponse(review.id, review.responseContent)}
                        >
                          <MessageCircle className="mr-2 h-4 w-4" />
                          {review.responseContent ? t('actions.editResponse') : t('actions.respond')}
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuItem
                            onClick={() => deleteMutation.mutate(review.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('actions.delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Package Info */}
              <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  {review.package.name}
                </span>
                {review.booking && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {review.booking.bookingReference}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-4 w-4" />
                  {review.helpfulCount} {t('helpful')}
                </span>
              </div>

              {/* Rating */}
              <div className="mt-4 flex items-center gap-3">
                {renderStars(review.rating)}
                <span className="text-sm text-muted-foreground">
                  {new Date(review.createdAt).toLocaleDateString()}
                </span>
                {review.travelType && (
                  <span className="bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {review.travelType}
                  </span>
                )}
              </div>

              {/* Content */}
              {review.title && (
                <h4 className="mt-4 font-medium text-foreground">{review.title}</h4>
              )}
              <p className="mt-2 text-muted-foreground whitespace-pre-line">{review.content}</p>

              {/* Response */}
              {review.responseContent && (
                <div className="mt-4 bg-muted p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">{t('yourResponse')}</p>
                    {review.respondedBy && (
                      <p className="text-xs text-muted-foreground">
                        {t('respondedBy')} {review.respondedBy.firstName} {review.respondedBy.lastName}
                      </p>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{review.responseContent}</p>
                </div>
              )}

              {/* Quick Actions for pending */}
              {canEdit && review.status === 'PENDING' && (
                <div className="mt-4 flex gap-2 pt-4 border-t border-border">
                  <Button
                    size="sm"
                    onClick={() => handleApprove(review.id)}
                    disabled={moderateMutation.isPending}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {t('actions.approve')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReject(review.id)}
                    disabled={moderateMutation.isPending}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    {t('actions.reject')}
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-sm text-muted-foreground">
            {t('page')} {data.pagination.page} {t('of')} {data.pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              {t('previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page === data.pagination.totalPages}
            >
              {t('next')}
            </Button>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-card p-6">
            <h3 className="text-lg font-semibold">{t('respondModal.title')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t('respondModal.subtitle')}</p>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              rows={5}
              placeholder={t('respondModal.placeholder')}
              className="mt-4 w-full border border-border p-3 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResponseModal(false);
                  setSelectedReview(null);
                  setResponseText('');
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSubmitResponse}
                disabled={!responseText.trim() || respondMutation.isPending}
              >
                {respondMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t('respondModal.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
